/**
 * LaneNav — compact 3-lane navigation strip used on the Wishlist and
 * Vault pages so the user can hop between the three sections of the app
 * without going back to the dashboard. The current lane is styled active
 * and acts as the page label. Each non-active lane links to its page.
 *
 * Use this on every page below the dashboard so the IA always feels
 * the same regardless of where the user landed. The dashboard itself
 * uses the larger ServiceHub component and doesn't need this strip.
 */
import { Link } from "react-router-dom";
import { Receipt, Heart, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

// Order matches the customer journey - and the ServiceHub on the
// dashboard - which is Wishlist (before buy) → Tracked purchases
// (after buy) → Vault (forever). Tracked purchases now has its own
// page at /dashboard/purchases so the link goes straight there, not
// to a now-removed anchor on the dashboard.
const LANES = [
  { id: "wishlist", label: "Wishlist", to: "/dashboard/wishlist", icon: Heart, accent: "#16A34A" },
  { id: "purchases", label: "Tracked purchases", to: "/dashboard/purchases", icon: Receipt, accent: "#002FA7" },
  { id: "vault", label: "Vault", to: "/dashboard/vault", icon: ShieldCheck, accent: "#D97706" },
];

export default function LaneNav({ active }) {
  return (
    <div className="border border-[#e4e4e7] bg-white mb-8 flex items-stretch" data-testid="lane-nav">
      <Link
        to="/dashboard"
        className="flex items-center gap-1.5 px-4 border-r border-[#e4e4e7] text-[11px] font-mono-pg uppercase tracking-widest text-[#52525b] hover:text-black hover:bg-[#f7f7f8] transition-colors"
        data-testid="lane-nav-back"
      >
        <ArrowLeft size={12} weight="bold" /> Hub
      </Link>
      {LANES.map((lane) => {
        const Icon = lane.icon;
        const isActive = lane.id === active;
        if (isActive) {
          return (
            <div
              key={lane.id}
              className="flex-1 px-4 py-3 flex items-center gap-2 border-r last:border-r-0 border-[#e4e4e7]"
              style={{ background: `${lane.accent}10`, borderTop: `2px solid ${lane.accent}` }}
              data-testid={`lane-nav-${lane.id}-active`}
            >
              <Icon size={16} weight="duotone" color={lane.accent} />
              <span className="font-bold text-sm" style={{ color: lane.accent }}>
                {lane.label}
              </span>
              <span className="pg-overline ml-auto" style={{ color: lane.accent }}>You are here</span>
            </div>
          );
        }
        return (
          <Link
            key={lane.id}
            to={lane.to}
            className="flex-1 px-4 py-3 flex items-center gap-2 border-r last:border-r-0 border-[#e4e4e7] hover:bg-[#f7f7f8] transition-colors group"
            data-testid={`lane-nav-${lane.id}`}
          >
            <Icon size={16} weight="duotone" color="#52525b" className="group-hover:text-[#0a0a0a] transition-colors" />
            <span className="font-semibold text-sm text-[#52525b] group-hover:text-[#0a0a0a] transition-colors">
              {lane.label}
            </span>
          </Link>
        );
      })}
    </div>
  );
}
